// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import { Directive, ElementRef, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FontAwesomeDetectorService, FontAwesomeFallbackService } from '@services';

@Directive({
  selector: '[lfxFaFallback]',
  standalone: false,
})
export class FontAwesomeFallbackDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private originalClasses: string[] = [];
  private originalContent = '';
  private originalTextContent = '';
  private fallbackApplied = false;
  private publicCdnLoaded = false;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private fontAwesomeDetector: FontAwesomeDetectorService,
    private fallbackService: FontAwesomeFallbackService
  ) {}

  ngOnInit(): void {
    // Store original state
    this.storeOriginalState();
    
    // Listen to Font Awesome status changes
    this.fontAwesomeDetector.fontAwesomeLoaded$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoaded => {
        if (isLoaded) {
          this.restoreOriginalIcon();
        } else {
          this.applyFontAwesomeFallback();
        }
      });

    // Check current status in case detection already completed
    if (this.fontAwesomeDetector.isCheckPerformed()) {
      const isLoaded = this.fontAwesomeDetector.isFontAwesomeLoaded();
      if (!isLoaded) {
        this.applyFontAwesomeFallback();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private storeOriginalState(): void {
    const element = this.el.nativeElement;
    this.originalClasses = Array.from(element.classList);
    this.originalContent = element.innerHTML;
    this.originalTextContent = element.textContent || '';
  }

  private async applyFontAwesomeFallback(): Promise<void> {
    if (this.fallbackApplied) return;

    const element = this.el.nativeElement;
    const currentClasses = Array.from(element.classList);
    
    // Check if this element has Font Awesome classes
    const faClasses = this.fallbackService.extractFontAwesomeClasses(currentClasses.join(' '));
    
    if (faClasses.length === 0) return; // Not a Font Awesome icon

    try {
      // Load public Font Awesome CDN if not already loaded
      if (!this.publicCdnLoaded) {
        await this.fallbackService.loadPublicFontAwesome();
        this.publicCdnLoaded = true;
      }

      // Get fallback Font Awesome class
      const fallbackClass = this.fallbackService.getFallbackClassForClasses(currentClasses);
      
      // Clear existing content
      this.renderer.setProperty(element, 'textContent', '');
      
      // Add fallback identification class
      this.renderer.addClass(element, 'fa-fallback-applied');
      
      // Remove original Font Awesome classes
      faClasses.forEach(faClass => {
        this.renderer.removeClass(element, faClass);
      });

      // Apply fallback Font Awesome classes from public CDN
      const fallbackClasses = fallbackClass.split(' ');
      fallbackClasses.forEach(className => {
        if (className.trim()) {
          this.renderer.addClass(element, className.trim());
        }
      });

      // Remove any emoji-specific styles
      this.renderer.removeStyle(element, 'font-family');
      this.renderer.removeStyle(element, 'font-style');
      this.renderer.removeStyle(element, 'font-weight');
      this.renderer.removeStyle(element, 'text-decoration');
      this.renderer.removeStyle(element, 'line-height');
      
      this.fallbackApplied = true;
      
      console.log(`FontAwesome Fallback: Replaced FA icon with fallback class "${fallbackClass}" for classes:`, faClasses);
    } catch (error) {
      console.error('FontAwesome Fallback: Failed to apply fallback', error);
    }
  }

  private restoreOriginalIcon(): void {
    if (!this.fallbackApplied) return;

    const element = this.el.nativeElement;
    
    // Restore original classes
    element.className = '';
    this.originalClasses.forEach(className => {
      this.renderer.addClass(element, className);
    });
    
    // Remove fallback class
    this.renderer.removeClass(element, 'fa-fallback-applied');
    
    // Restore original content
    this.renderer.setProperty(element, 'innerHTML', this.originalContent);
    
    // Remove inline styles
    this.renderer.removeStyle(element, 'font-family');
    this.renderer.removeStyle(element, 'font-style');
    this.renderer.removeStyle(element, 'font-weight');
    this.renderer.removeStyle(element, 'text-decoration');
    this.renderer.removeStyle(element, 'line-height');
    
    this.fallbackApplied = false;
    
    console.log('FontAwesome Fallback: Restored original Font Awesome icon');
  }
}
